import { createWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { immer } from "zustand/middleware/immer";
import { persist, createJSONStorage } from "zustand/middleware";
import { mergeDeepLeft } from "ramda";
import devDebug from "../Helpers/debug";
import idbKeyvalAdapter from "./1_utility";
// import { current } from "immer";


// define the initial state
const initialState = {
  cpvStatSearch: "",
  awardsDropDownOpen: false,
  showHelpVideoModal: false,
  showUnsavedPopup: false,
  showMultilanguageVideo: false,
  showUpgradePopup: false,
  showNoResultsMessage: false,
  finishedCounter: 0,
  nutsSearchFromDashboardClick: "",
  showCompanyModal: false,
  companyIDToGet: 0,
  companyNameToGet: "",
  tenderingAuthorityNameToGet: "",
  showTAModal: false,
  tenderingAuthorityIDToGet: 0,
  awardsTableConstraintType: "",
  awardsTableConstraintID: null,
  awardsTableConstraintName: "",
  isDark: false,
  input: "",
  hideListNutsSearch: true,
  applyModal: false,
  setApplyModalContent: "apply",
  deduplicateCallTime: {},
  showDislikeWarningModal: false,
  dislikeWarningModalShown: false,
  cookieState: { userHasInteracted: 0, cookiesEnabled: true },
  cookieModalVisible: false,
  remoteFullUserReset: {},
  showRefreshWarningWithLogout: false,
  showRefreshWarningWithoutLogout: false, //the version tracking for loginDataVersion happens in the dedicated versionChangeStore
};
const currentAppVersionFromEnv = Number(import.meta.env.VITE_VERSION);

export const useComponentsStore = createWithEqualityFn(
  persist(
    immer((set, get) => ({
      ...initialState,
      // ------------------- VERSION CHECKING --------------
      //the version tracking for loginDataVersion / otherDataVersion etc. happens in the dedicated versionChangeStore
      setShowRefreshWarningWithLogout: (showRefreshWarningWithLogout) =>
        set((state) => {
          devDebug("setShowRefreshWarningWithLogout being set to: ", showRefreshWarningWithLogout);
          state.showRefreshWarningWithLogout = showRefreshWarningWithLogout;
        }),
      setShowRefreshWarningWithoutLogout: (showRefreshWarningWithoutLogout) =>
        set((state) => {
          devDebug("setShowRefreshWarningWithoutLogout being set to: ", showRefreshWarningWithoutLogout);
          state.showRefreshWarningWithoutLogout = showRefreshWarningWithoutLogout;
        }),
      setRemoteFullUserReset: (remoteFullUserReset) =>
        set((state) => {
          state.remoteFullUserReset = remoteFullUserReset;
        }),
      // ------------------- COOKIE NOTICE AND DARK MODE --------------
      setCookieModalVisible: (cookieModalVisible) =>
        set((state) => {
          state.cookieModalVisible = cookieModalVisible;
        }),
      setCookieState: (cookieState) =>
        set((state) => {
          state.cookieState = cookieState;
        }),
      setIsDark: (payload) =>
        set((state) => {
          state.isDark = payload;
        }),
      // ------------------- MODALS --------------
      setShowDislikeWarningModal: (showDislikeWarningModal) =>
        set((state) => {
          state.showDislikeWarningModal = showDislikeWarningModal
        }),
      setDislikeWarningModalShown: (dislikeWarningModalShown) =>
        set((state) => {
          state.dislikeWarningModalShown = dislikeWarningModalShown
        }),
      // ------------------- UTILITY FUNCTIONS --------------

      setDeduplicateCallTime: (key, time) =>
        set((state) => {
          state.deduplicateCallTime[key] = time
        }),
      // ------------------- VIEW / EDIT TENDER --------------
      setApplyModalContent: (applyModalContent) =>
        set((state) => {
          state.applyModalContent = applyModalContent;
        }),
      setApplyModal: (applyModal) =>
        set((state) => {
          state.applyModal = applyModal;
        }),
      setInput: (payload) =>
        set((state) => {
          state.input = payload;
        }),
      setHideListNutsSearch: (payload) =>
        set((state) => {
          state.hideListNutsSearch = payload;
        }),
      helpVideoAction: (payload) =>
        set((state) => {
          state.showHelpVideoModal = payload;
        }),
      setShowCompanyModal: (payload) =>
        set((state) => {
          state.showCompanyModal = payload;
        }),
      setAwardsDropDownOpen: (payload) =>
        set((state) => {
          state.awardsDropDownOpen = payload;
        }),
      setCompanyIDToGet: (payload) =>
        set((state) => {
          state.companyIDToGet = payload;
        }),
      setCompanyNameToGet: (payload) =>
        set((state) => {
          state.companyNameToGet = payload;
        }),
      setTenderingAuthorityNameToGet: (payload) =>
        set((state) => {
          state.tenderingAuthorityNameToGet = payload;
        }),
      setShowTAModal: (payload) =>
        set((state) => {
          state.showTAModal = payload;
        }),
      setAwardsTableConstraint: (type, id, name) =>
        set((state) => {
          state.awardsTableConstraintType = type;
          state.awardsTableConstraintID = id;
          state.awardsTableConstraintName = name;
        }),
      setTenderingAuthorityIDToGet: (payload) =>
        set((state) => {
          state.tenderingAuthorityIDToGet = payload;
        }),
      setShowUnsavedPopup: (payload) =>
        set((state) => {
          state.showUnsavedPopup = payload;
        }),
      multiLanguageVideoAction: (payload) =>
        set((state) => {
          state.showMultilanguageVideo = payload;
        }),
      setShowUpgradePopup: (payload) =>
        set((state) => {
          state.showUpgradePopup = payload;
        }),
      setStatSearch: (payload) =>
        set((state) => {
          state.cpvStatSearch = payload;
        }),
      setShowNoResultsMessage: (payload) =>
        set((state) => {
          state.showNoResultsMessage = payload;
        }),
      setFinishedCounter: (payload) =>
        set((state) => {
          state.finishedCounter += 1;
        }),
      setNutsSearchFromDashboardClick: (payload) =>
        set((state) => {
          state.nutsSearchFromDashboardClick = payload;
        }),
      resetState: () => {
        useComponentsStore.persist.clearStorage();
        set(initialState);
      },
    })),
    {
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => ![
            "cpvStatSearch",
            "awardsDropDownOpen",
            "showHelpVideoModal",
            "showUnsavedPopup",
            "showMultilanguageVideo",
            "showUpgradePopup",
            "showNoResultsMessage",
            "finishedCounter",
            "nutsSearchFromDashboardClick",
            "showCompanyModal",
            "companyIDToGet",
            "companyNameToGet",
            "tenderingAuthorityNameToGet",
            "showTAModal",
            "tenderingAuthorityIDToGet",
            "awardsTableConstraintType",
            "awardsTableConstraintID",
            "awardsTableConstraintName",
            "isDark",
            "input",
            "hideListNutsSearch",
            "applyModal",
            "setApplyModalContent",
            "deduplicateCallTime",
            "showRefreshWarningWithLogout",
            "showRefreshWarningWithoutLogout",
          ].includes(key))
        ),
      name: "componentsStore",
      storage: createJSONStorage(() => idbKeyvalAdapter),
      version: currentAppVersionFromEnv,
      migrate: (persistedState, storedVersion) => {
        devDebug(
          `[componentsStore Migrate] Called! Stored: ${storedVersion} (type: ${typeof storedVersion}), App: ${currentAppVersionFromEnv} (type: ${typeof currentAppVersionFromEnv})`
        );
        if (storedVersion !== currentAppVersionFromEnv) {
          devDebug('[componentsStore Migrate] Versions differ. Resetting to initialState.');
          return initialState;
        }
        if (persistedState === null || persistedState === undefined) {
          devDebug('[componentsStore Migrate] Persisted state is null/undefined despite version match. Resetting.');
          return initialState;
        }
        devDebug('[componentsStore Migrate] Versions same. Using persisted state.');
        return persistedState;
      },
      // merge: (persistedState, currentState) => {
      //   devDebug('[componentsStore Merge] Merging state...');
      //   if (typeof persistedState !== 'object' || persistedState === null) {
      //     devDebug('[componentsStore Merge] Persisted state is not an object, returning current state or initialState.');
      //     return initialState;
      //   }
      //   return mergeDeepLeft(persistedState, currentState);
      // },
      onRehydrateStorage: (state) => {
        devDebug(`[componentsStore onRehydrateStorage] Attempting rehydration...`);
        return (hydratedState, error) => {
          if (error) {
            console.error(
              `[componentsStore] Failed to rehydrate store!`,
              error
            );
            devDebug(
              `[componentsStore] Error during rehydration. Store name: "componentsStore".`,
              "Error object:", error,
              "State passed to onRehydrateStorage:", state, // This is the raw value from storage before parsing/migration
              "Hydrated state (if available):", hydratedState // This is the state *after* potential migration/parsing, if it got that far
            );
          } else {
            // devDebug(
            //   `[componentsStore] Successfully rehydrated. Hydrated state:`,
            //   hydratedState
            // );
          }
        };
      },
    }
  ),
  shallow
);
